import { Stack } from '@mui/material';
import { useWatch } from 'react-hook-form';

import { FormAwareCheckbox } from '@/components/form/formAwareInputs/FormAwareCheckbox';
import { FormAwareSelectInput } from '@/components/form/formAwareInputs/FormAwareSelectInput';
import { FormLayoutItem, FormLayoutRow } from '@/components/layout/FormLayout';
import { PopperContent } from '@/components/poppers/PopperContent';
import { useFormContext } from '@/components/react-hook-form';
import { ContextualHelpTrigger } from '@/modules/content/components/ContextualHelpTrigger';
import { EstateWaterfallHypotheticalTransferTransferTaxKind } from '@/types/schema';

import { useHypotheticalTransferType } from '../hooks/useHypotheticalTransferType';
import { HypotheticalTransferFormModalShape } from '../HypotheticalTransferModal.types';
import { deserializeTransferType } from '../HypotheticalTransferModal.utils';
import { TransferTypeCallout } from './TransferTypeCallout';

export function TransferType() {
  const { control } = useFormContext<HypotheticalTransferFormModalShape>();
  const transferTypes = useHypotheticalTransferType();
  const transferType = useWatch({ control, name: 'transferType' });

  const { transferTaxKind } = deserializeTransferType(transferType);
  const isTaxableGift =
    transferTaxKind ===
    EstateWaterfallHypotheticalTransferTransferTaxKind.GrantorTaxableGift;

  return (
    <Stack>
      <FormLayoutRow
        sx={{
          alignItems: 'flex-start', // don't shift the "GST gift" checkbox if help text is shown
        }}
      >
        <FormLayoutItem width={isTaxableGift ? 8 : 12}>
          <FormAwareSelectInput<HypotheticalTransferFormModalShape>
            control={control}
            fieldName="transferType"
            label="Type of transfer (gift tax purposes)"
            required
            options={transferTypes}
            contextualHelp={
              <PopperContent
                body="Learn more about transfer types and associated estate tax treatments"
                actions={
                  <ContextualHelpTrigger
                    contextualHelpId="transferTypes"
                    display="Learn more"
                  />
                }
                actionsSx={{ marginBottom: 0, marginRight: 0 }}
              />
            }
          />
        </FormLayoutItem>
        {isTaxableGift && (
          <FormLayoutItem
            width={4}
            sx={{
              pt: 0.7, // offset so it aligns with the select element next to it
            }}
          >
            <Stack justifyContent="center" height="100%" pt={2}>
              <FormAwareCheckbox<HypotheticalTransferFormModalShape>
                control={control}
                fieldName="transferGstGift"
                label="GST Gift"
                contextualHelp={
                  <PopperContent body="If selected, GST exemption will be used regardless of the recipient type" />
                }
              />
            </Stack>
          </FormLayoutItem>
        )}
      </FormLayoutRow>
      <TransferTypeCallout />
    </Stack>
  );
}
